import { aiSettingsToAiReplyConfig } from "@redotech/effect-api-shared/default-rules/support-settings-mapping";
import {
  socialChannelGaps,
  type SupportInsightId,
  type SupportInsightSignals,
} from "./qualify-support-insights";
import type { SupportInsightLocationId } from "./support-insight-locations";

/** Same rep and salary assumptions as the Support dashboard cost-savings card. */
const ANNUAL_SALARY = 40_150;
const WEEKS_PER_YEAR = 48;
const TICKETS_PER_DAY = 60;
const WORK_DAYS_PER_WEEK = 5;
const HOURS_PER_WORK_WEEK = 40;
const TICKETS_PER_REP_WEEK = TICKETS_PER_DAY * WORK_DAYS_PER_WEEK;
const COST_PER_TICKET = ANNUAL_SALARY / (WEEKS_PER_YEAR * TICKETS_PER_REP_WEEK);
const HOURS_PER_TICKET = HOURS_PER_WORK_WEEK / TICKETS_PER_REP_WEEK;

/** Same channels the email toggle adds in `aiSettingsToAiReplyConfig`. */
const EMAIL_AI_CHANNELS = aiSettingsToAiReplyConfig({
  autopilotEnabled: true,
  enabledPlatforms: { email: true },
}).configurations.channels;

/** Ticket totals for one merchant over `windowDays`. */
export interface SupportInsightVolume {
  windowDays: number;
  aiResolved: number;
  humanResolved: number;
  aiMedianResolutionMs: number;
  humanMedianResolutionMs: number;
  channels: readonly { channel: string; count: number }[];
}

export interface SupportInsightFigures {
  extraHoursPerWeek: number;
  extraDollarsPerYear: number;
  currentReps: number;
  potentialReps: number;
  extraHoursPerMonth: number;
  percentFaster: number;
}

const ZERO_FIGURES: SupportInsightFigures = {
  extraHoursPerWeek: 0,
  extraDollarsPerYear: 0,
  currentReps: 0,
  potentialReps: 0,
  extraHoursPerMonth: 0,
  percentFaster: 0,
};

function channelCount(
  volume: SupportInsightVolume,
  names: readonly string[],
): number {
  return volume.channels.reduce(
    (sum, row) => (names.includes(row.channel) ? sum + row.count : sum),
    0,
  );
}

/** Share of assigned tickets AI resolves, capped at 1. */
function automationRate(
  signals: SupportInsightSignals,
  volume: SupportInsightVolume,
): number {
  const total = volume.aiResolved + volume.humanResolved;
  if (total <= 0) {
    return 0;
  }
  const overall = volume.aiResolved / total;
  const assignment = signals.autopilotTicketPercent / 100;
  if (assignment <= 0) {
    return Math.min(1, overall);
  }
  return Math.min(1, overall / assignment);
}

function extraTickets(
  id: SupportInsightId,
  signals: SupportInsightSignals,
  volume: SupportInsightVolume,
): number {
  const rate = automationRate(signals, volume);
  const total = volume.aiResolved + volume.humanResolved;
  switch (id) {
    case "ai-ticket-volume": {
      const assignment = signals.autopilotTicketPercent / 100;
      if (assignment >= 1) {
        return 0;
      }
      return total * (1 - assignment) * rate;
    }
    case "email-channel":
      return channelCount(volume, EMAIL_AI_CHANNELS) * rate;
    case "widget-ai":
      return channelCount(volume, ["redoChat"]) * rate;
    case "widget-enabled":
      return 0;
    case "social-ai":
      return (
        socialChannelGaps(signals).reduce(
          (count, gap) => count + channelCount(volume, [gap.channel]),
          0,
        ) * rate
      );
  }
}

function scale(count: number, windowDays: number, days: number): number {
  if (windowDays <= 0) {
    return 0;
  }
  return (count * days) / windowDays;
}

export function supportInsightFigures(
  id: SupportInsightId,
  signals: SupportInsightSignals,
  volume: SupportInsightVolume,
): SupportInsightFigures {
  if (volume.windowDays <= 0) {
    return ZERO_FIGURES;
  }
  const extra = extraTickets(id, signals, volume);
  const weeklyExtra = scale(extra, volume.windowDays, 7);
  const weeklyAi = scale(volume.aiResolved, volume.windowDays, 7);
  const extraHoursPerWeek = weeklyExtra * HOURS_PER_TICKET;
  const human = volume.humanMedianResolutionMs;
  const ai = volume.aiMedianResolutionMs;
  const percentFaster =
    volume.aiResolved > 0 && human > 0 && ai >= 0 && ai < human
      ? ((human - ai) / human) * 100
      : 0;
  return {
    extraHoursPerWeek,
    extraDollarsPerYear: weeklyExtra * WEEKS_PER_YEAR * COST_PER_TICKET,
    currentReps: weeklyAi / TICKETS_PER_REP_WEEK,
    potentialReps: (weeklyAi + weeklyExtra) / TICKETS_PER_REP_WEEK,
    extraHoursPerMonth: extraHoursPerWeek * (365 / 12 / 7),
    percentFaster,
  };
}

export function formatInsightHours(hours: number): string {
  if (!Number.isFinite(hours) || hours <= 0) {
    return "0";
  }
  const rounded = hours >= 10 ? Math.round(hours) : Math.round(hours * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

export function formatInsightDollars(dollars: number): string {
  const amount = Number.isFinite(dollars)
    ? Math.max(0, Math.round(dollars))
    : 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatInsightReps(reps: number): string {
  if (!Number.isFinite(reps) || reps <= 0) {
    return "0";
  }
  const rounded = Math.round(reps * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

export function formatInsightPercent(percent: number): string {
  if (!Number.isFinite(percent) || percent <= 0) {
    return "0";
  }
  return String(Math.round(percent));
}

/** Metric cards and the Teams page banner that have official insight copy. */
export type SupportInsightMessageLocation =
  | SupportInsightLocationId
  | "teams-page-banner";

const supportInsightCopyLocations = new Set<SupportInsightMessageLocation>([
  "ticket-automation-rate",
  "total-tickets-automated",
  "potential-cost-savings",
  "median-time-to-resolution",
  "teams-page-banner",
]);

export function supportInsightLocationHasCopy(
  location: SupportInsightMessageLocation,
): boolean {
  return supportInsightCopyLocations.has(location);
}

const networkList = new Intl.ListFormat("en", {
  style: "long",
  type: "conjunction",
});

interface InsightActionPhrase {
  taking: string;
  turnOn: string;
}

function socialNetworkNames(signals: SupportInsightSignals): string {
  const networks: string[] = [];
  for (const gap of socialChannelGaps(signals)) {
    if (!networks.includes(gap.network)) {
      networks.push(gap.network);
    }
  }
  return networkList.format(networks) || "Facebook and Instagram";
}

function insightActionPhrase(
  id: SupportInsightId,
  signals: SupportInsightSignals,
): InsightActionPhrase {
  switch (id) {
    case "ai-ticket-volume":
      return {
        taking: "raising the percent of tickets assigned to AI",
        turnOn: "a higher percent of tickets",
      };
    case "email-channel":
      return { taking: "turning on AI for email", turnOn: "email" };
    case "widget-ai":
      return {
        taking: "turning on AI for the chat widget",
        turnOn: "the chat widget",
      };
    case "widget-enabled":
      return {
        taking: "turning on the chat widget",
        turnOn: "the chat widget",
      };
    case "social-ai": {
      const names = socialNetworkNames(signals);
      return { taking: `turning on AI for ${names}`, turnOn: names };
    }
  }
}

/** Short copy for a location, action, and this merchant's ticket totals. */
export function supportInsightMessage(
  location: SupportInsightMessageLocation,
  id: SupportInsightId,
  signals: SupportInsightSignals,
  volume: SupportInsightVolume,
): string | undefined {
  const action = insightActionPhrase(id, signals);
  const figures = supportInsightFigures(id, signals, volume);
  switch (location) {
    case "ticket-automation-rate":
    case "total-tickets-automated":
      if (id === "widget-enabled") {
        return undefined;
      }
      return `Save +${formatInsightHours(figures.extraHoursPerWeek)} hours/week by ${action.taking}.`;
    case "potential-cost-savings":
      if (id === "widget-enabled") {
        return undefined;
      }
      return `Save +${formatInsightDollars(figures.extraDollarsPerYear)}/yr with AI by ${action.taking}.`;
    case "median-time-to-resolution":
      return `Resolve tickets ${formatInsightPercent(figures.percentFaster)}% faster ${action.taking}.`;
    case "teams-page-banner":
      return `AI does the work of ${formatInsightReps(figures.currentReps)} reps but could do ${formatInsightReps(figures.potentialReps)}. Turn it on for ${action.turnOn} to save ${formatInsightHours(figures.extraHoursPerMonth)} more hours a month.`;
    case "closed-tickets":
    case "created-tickets":
    case "median-first-human-response":
    case "median-first-response-time":
    case "resolved-tickets":
    case "total-tickets":
      return undefined;
  }
}
